export const enum RetrievalMode {
    Hybrid = "hybrid",
    Vectors = "vectors",
    Text = "text"
}

export type ChatAppRequestOverrides = {
    consumer_id?: string;
    consumer_ids?: string[];
    consumer_names?: string[];
    user_id?: string;
    note_types?: string[];
    form_names?: string[];
    service_codes?: string[];
    clinical_activities?: string[];
    assessment_scales?: string[];
    service_programs?: string[];
    service_descriptions?: string[];
    providers?: string[];
    case_managers?: string[];
    assessment_staff?: string[];
    date_years?: string[];
    severity_indicators?: string[];
    clinical_top?: number;
    context_perspective?: "focused_rag" | "whole_patient_review";
    retrieval_effort?: "quick" | "balanced" | "best";
    sort_order?: "newest" | "oldest";
    retrieval_mode?: RetrievalMode;
    semantic_ranker?: boolean;
    semantic_captions?: boolean;
    query_rewriting?: boolean;
    reasoning_effort?: string;
    include_category?: string;
    exclude_category?: string;
    seed?: number;
    top?: number;
    retrieval_reasoning_effort?: string;
    temperature?: number;
    minimum_search_score?: number;
    minimum_reranker_score?: number;
    prompt_template?: string;
    prompt_template_name?: string;
    prompt_template_prefix?: string;
    prompt_template_suffix?: string;
    suggest_followup_questions?: boolean;
    send_text_sources: boolean;
    send_image_sources: boolean;
    search_text_embeddings: boolean;
    search_image_embeddings: boolean;
    search_index?: string; // "avsearchv3" or "company-docs"
    language: string;
    use_agentic_knowledgebase: boolean;
    agentic_clinical_retrieval?: boolean;
    use_web_source?: boolean;
    use_sharepoint_source?: boolean;
    clinical_chat_graph?: "off" | "trace" | "supplemental" | "fusion";
    clinical_chat_query_rewrite?: "auto" | "deterministic" | "llm";
    clinical_chat_runtime_planner?: "deterministic" | "llm";
    experimental_reranker?: "disabled" | "lexical";
    show_graph_supplemental_evidence?: boolean;
    model?: string;
    content_mode?: "summary" | "full" | "semantic";
    selected_note_scope?: SelectedNoteScopeRequest;
};

export type ClinicalChatRuntimeControls = {
    graph: {
        default: "off" | "trace" | "supplemental" | "fusion";
        allowedValues: ("off" | "trace" | "supplemental" | "fusion")[];
        requestOverride: "clinical_chat_graph";
        mutability: "request-override-safe";
    };
    queryRewrite: {
        default: "auto" | "deterministic" | "llm";
        allowedValues: ("auto" | "deterministic" | "llm")[];
        requestOverride: "clinical_chat_query_rewrite";
        mutability: "request-override-safe";
    };
    runtimePlanner: {
        default: "deterministic" | "llm";
        allowedValues: ("deterministic" | "llm")[];
        requestOverride: "clinical_chat_runtime_planner";
        mutability: "request-override-safe-diagnostic";
    };
    reranker: {
        default: "disabled" | "lexical";
        allowedValues: ("disabled" | "lexical")[];
        requestOverride: "experimental_reranker";
        mutability: "request-override-safe";
    };
};

export type SelectedNoteScopeRequest = {
    status: "active";
    mode: "selected_notes" | "all_current_browse_results";
    note_ids?: string[];
    patient_ids: string[];
    source: "browse";
    source_filters?: {
        noteTypes?: string[];
        formNames?: string[];
        serviceCodes?: string[];
        servicePrograms?: string[];
        serviceDescriptions?: string[];
        providers?: string[];
        caseManagers?: string[];
        assessmentStaff?: string[];
        dateRanges?: string[];
        severityIndicators?: string[];
        sortOrder?: string;
    };
    label?: string;
};

export type ResponseMessage = {
    content: string;
    role: string;
};

export type Thoughts = {
    title: string;
    description: any; // It can be any output from the api
    props?: { [key: string]: any };
};

export type ActivityDetail = {
    id?: number;
    number?: number;
    type?: string;
    label?: string;
    source?: string;
    query?: string;
};

export type ExternalResultMetadata = {
    id?: string;
    title?: string;
    url?: string;
    snippet?: string;
    activity?: ActivityDetail;
};

export type CitationActivityDetail = {
    id?: string;
    number?: number;
    type?: string;
    source?: string;
    query?: string;
};

export type DataPoints = {
    text: string[];
    images: string[];
    citations: string[];
    citation_ids?: Record<string, string>;
    citation_activity_details?: Record<string, CitationActivityDetail>;
    citation_highlights?: Record<string, string[]>; // AssessmentId -> highlighted fragments
    retrieval_diagnostics?: Record<string, unknown>;
    external_results_metadata?: ExternalResultMetadata[];
    supplemental_graph_evidence?: SupplementalGraphEvidencePayload;
};

export type SupplementalGraphEvidenceSpan = {
    section_label?: string | null;
    statement_label?: string | null;
    text?: string | null;
    offset_start?: number | null;
    offset_end?: number | null;
};

export type SupplementalGraphEvidenceItem = {
    assessment_id: string;
    citation_label?: string;
    note_type?: string | null;
    assessment_date?: string | null;
    source_origin: "context_graph";
    review_status: "reviewed_relevant";
    reason?: string;
    relationship_summary?: string | null;
    evidence_spans?: SupplementalGraphEvidenceSpan[];
};

export type SupplementalGraphEvidencePayload = {
    status: "disabled" | "blocked" | "empty" | "available";
    source_scope?: string;
    presentation?: {
        label?: string;
        helper_text?: string;
        collapsed_by_default?: boolean;
    };
    reason?: string;
    graph_status?: string;
    answer_used_removed_count?: number;
    items: SupplementalGraphEvidenceItem[];
};

export type GraphSourceOrigin = "semantic" | "context_graph" | "both" | "structured" | "manual";

export type GraphNode = {
    id: string;
    type: string;
    label: string;
    clinical_type?: string;
    source_origin: GraphSourceOrigin;
    assessment_id?: string;
    consumer_id?: string;
    metadata?: Record<string, unknown>;
};

export type GraphEdge = {
    id: string;
    source: string;
    target: string;
    type: string;
    source_origin: GraphSourceOrigin;
    label?: string;
    metadata?: Record<string, unknown>;
};

export type GraphPayload = {
    nodes: GraphNode[];
    edges: GraphEdge[];
    metadata: {
        node_count: number;
        edge_count: number;
        found?: boolean;
        bounded?: boolean;
        assessment_ids?: string[];
        consumer_id?: string;
        status?: string;
        reason?: string;
        [key: string]: unknown;
    };
};

export type TimingBreakdown = {
    query_rewrite_ms?: number;
    embedding_ms?: number;
    search_ms?: number;
    content_extraction_ms?: number;
    agentic_retrieval_ms?: number;
    retrieval_total_ms?: number;
    llm_ttft_ms?: number;
    llm_generation_ms?: number;
    total_backend_ms?: number;
};

export type CostSummary = {
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_reasoning_tokens: number;
    total_tokens: number;
    model: string;
    provider_type?: string;
    provider_name?: string;
    estimated_cost?: number;
    elapsed_ms?: number;
    subquery_count?: number;
    disclaimer?: string;
    timing?: TimingBreakdown;
};

export type ResponseContext = {
    data_points: DataPoints;
    followup_questions: string[] | null;
    thoughts: Thoughts[];
    answer?: string;
    cost_summary?: CostSummary;
};

export type ChatAppResponseOrError = {
    message: ResponseMessage;
    delta: ResponseMessage;
    context: ResponseContext;
    session_state: any;
    error?: string;
};

export type ChatAppResponse = {
    message: ResponseMessage;
    delta: ResponseMessage;
    context: ResponseContext;
    session_state: any;
};

export type ChatAppRequestContext = {
    overrides?: ChatAppRequestOverrides;
};

export type ChatAppRequest = {
    messages: ResponseMessage[];
    context?: ChatAppRequestContext;
    session_state: any;
    patId?: string;
    userId?: string;
};

export type Config = {
    defaultReasoningEffort: string;
    defaultRetrievalReasoningEffort: string;
    showMultimodalOptions: boolean;
    showSemanticRankerOption: boolean;
    showQueryRewritingOption: boolean;
    showReasoningEffortOption: boolean;
    streamingEnabled: boolean;
    showVectorOption: boolean;
    showUserUpload: boolean;
    showLanguagePicker: boolean;
    showSpeechInput: boolean;
    showSpeechOutputBrowser: boolean;
    showSpeechOutputAzure: boolean;
    showChatHistoryBrowser: boolean;
    showChatHistoryCosmos: boolean;
    showAgenticRetrievalOption: boolean;
    defaultClinicalChatGraph: "off" | "trace" | "supplemental" | "fusion";
    defaultClinicalChatQueryRewrite: "auto" | "deterministic" | "llm";
    defaultClinicalChatRuntimePlanner: "deterministic" | "llm";
    defaultClinicalChatReranker: "disabled" | "lexical";
    clinicalChatRuntimeControls?: ClinicalChatRuntimeControls;
    ragSearchTextEmbeddings: boolean;
    ragSearchImageEmbeddings: boolean;
    ragSendTextSources: boolean;
    ragSendImageSources: boolean;
    webSourceEnabled: boolean;
    sharepointSourceEnabled: boolean;
    graphSupplementalEvidenceUiEnabled: boolean;
};

export type SimpleAPIResponse = {
    message?: string;
};

export interface SpeechConfig {
    speechUrls: (string | null)[];
    setSpeechUrls: (urls: (string | null)[]) => void;
    audio: HTMLAudioElement;
    isPlaying: boolean;
    setIsPlaying: (isPlaying: boolean) => void;
}

export type HistoryListApiResponse = {
    sessions: {
        id: string;
        entra_oid: string;
        title: string;
        timestamp: number;
        patient_id?: string;
        total_tokens?: number;
        estimated_cost?: number;
        query_count?: number;
        total_backend_ms?: number;
    }[];
    continuation_token?: string;
};

export type HistoryApiResponse = {
    id: string;
    entra_oid: string;
    answers: any;
};
// Browse feature types
export type BrowseNoteItem = {
    id: string;
    consumer_id?: string;
    consumer_name?: string;
    note_type: string;
    service_date: string;
    provider_name: string;
    snippet?: string;
    // Summary fields
    briefSummary?: string;
    summary?: string;
    severityIndicator?: string;
    riskFlags?: string;
    wellnessFlags?: string;
    keyConcerns?: string;
    significantEvents?: string;
    traumaContext?: string;
    lifeEvents?: string;
};

export type BrowseNotesResponse = {
    notes: BrowseNoteItem[];
    total_count: number;
    skip: number;
    top: number;
};
// Patient Brief types
export type PatientBriefValidation = {
    passed: boolean | null;
    highRiskHallucinations: string[] | null;
    lowRiskIssues: string[] | null;
    verifiedClaims: string[] | null;
};

export type PatientBriefSummaryItem = {
    label: string;
    value: string;
};

export type PatientBriefTrajectoryCard = {
    title: string;
    subtitle?: string | null;
    detail?: string | null;
    items: PatientBriefSummaryItem[];
};

export type PatientBriefSummary = {
    patientName?: string | null;
    currentStatus?: PatientBriefSummaryItem[];
    trajectory?: PatientBriefTrajectoryCard[];
    criticalHistory?: string[];
    careContext?: string[];
    sessionFocus?: string[];
};

export type PatientBriefResponse = {
    found: boolean;
    reason?: string;
    lastStatus?: string;
    lastError?: string;
    id?: number;
    runId?: number;
    narrative?: string;
    summary?: PatientBriefSummary;
    generationPrompt?: string;
    tokenCount?: number;
    generationLatencyMs?: number;
    validation?: PatientBriefValidation;
    finalStatus?: string;
    createdAt?: string;
    model?: string;
    promptVersion?: string;
    runDescription?: string;
};

export type TimelineEvent = {
    id: number;
    eventDate: string;
    eventType: string;
    eventCategory: string;
    eventDescription: string | null;
    severity: string | null;
    sourceTable: string | null;
    sourceReferenceId: string | null;
    isPinned: boolean;
    clinicalNotes: string | null;
};

export type TimelineCategory = {
    category: string;
    count: number;
};

export type PatientTimelineResponse = {
    consumerId: string;
    events: TimelineEvent[];
    totalCount: number;
    categories: TimelineCategory[];
    filters: {
        months: number | null;
        category: string | null;
    };
};
export interface PatientDemographics {
    identity: {
        preferredName: string | null;
        age: number | null;
        dob: string | null;
        sex: string | null;
        genderIdentity: string | null;
        pronouns: string | null;
        raceEthnicity: string | null;
        language: string | null;
    };
    clinical: {
        primaryDiagnosis: string | null;
        levelOfCare: string | null;
        zeroSuicidePathway: string | null;
        wasEverSUD: string | null;
    };
    careTeam: {
        caseManager: string | null;
        medicalProvider: string | null;
        therapist: string | null;
        pcp: string | null;
    };
    status: {
        isOpen: string | null;
        certifiedNow: string | null;
        certificationType: string | null;
        certifiedLast30Days: string | null;
        nextApptDate: string | null;
        latestServiceDate: string | null;
        latestTxPlanDate: string | null;
    };
}
export interface PatientCaseReview {
    found: boolean;
    message?: string;
    assessmentId?: string;
    assessmentDate?: string;
    content?: string;
    staff?: string;
}
